import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { analytics } from './Firebase';
import { setAnalyticsCollectionEnabled, logEvent } from 'firebase/analytics';
import './CookieConsent.css';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentProps {
  className?: string;
  onConsentChange?: (hasConsent: boolean) => void;
  privacyPolicyUrl: string;
  cookiePolicyUrl?: string;
  onPolicyClick?: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({
  className = '',
  onConsentChange,
  privacyPolicyUrl,
  cookiePolicyUrl,
  onPolicyClick
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check both storage locations for existing preferences
    const localPreferences = localStorage.getItem('cookieConsent');
    const sessionPreferences = sessionStorage.getItem('cookieConsent');
    
    const savedConsent = localPreferences || sessionPreferences;
    
    if (savedConsent) {
      const savedPreferences = JSON.parse(savedConsent) as CookiePreferences;
      setPreferences(savedPreferences);
      setHasConsent(true);
      setAnalyticsCollectionEnabled(analytics, savedPreferences.analytics);
    } else {
      setIsVisible(true);
    }
  }, []);

  useEffect(() => {
    if (onConsentChange) {
      onConsentChange(hasConsent);
    }
  }, [hasConsent, onConsentChange]);

  // Listen for navigation back from policy pages
  useEffect(() => {
    const handleNavigation = () => {
      if (!window.location.pathname.includes('privacyPolicy') && 
          !window.location.pathname.includes('cookiePolicy')) {
        setIsVisible(true);
      }
    };

    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, []);

  const handlePolicyClick = () => {
    setIsVisible(false);
    onPolicyClick?.();
  };

  const handleAcceptAll = () => {
    const newPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    savePreferences(newPreferences);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  const savePreferences = async (prefs: CookiePreferences) => {
    const allPermissionsGranted = Object.values(prefs).every(value => value === true);
    
    // Store in either local or session storage based on permissions
    if (allPermissionsGranted) {
      localStorage.setItem('cookieConsent', JSON.stringify(prefs));
      console.log('All permissions granted - storing in local storage');
    } else {
      sessionStorage.setItem('cookieConsent', JSON.stringify(prefs));
      console.log('Some permissions denied - storing in session storage');
    }

    setHasConsent(true);
    setIsVisible(false);
    
    // Enable/disable analytics
    await setAnalyticsCollectionEnabled(analytics, prefs.analytics);
    
    if (prefs.analytics) {
      try {
        logEvent(analytics, 'cookie_consent_given');
        console.log('Analytics enabled successfully - test event sent');
      } catch (error) {
        console.error('Analytics may not be enabled:', error);
      }
    } else {
      console.log('Analytics disabled by user preference');
    }
  };

  if (!isVisible || hasConsent) return null;

  return (
    <div className="cookie-consent-overlay">
      <div className={`cookie-consent ${className}`}>
        <div className="cookie-consent-container">
          {!showPreferences ? (
            <div className="cookie-banner">
              <p>
                We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies. Learn more in our{' '}
                <Link 
                  to={privacyPolicyUrl} 
                  onClick={handlePolicyClick}
                  className="policy-link"
                >
                  Privacy Policy
                </Link>
                {cookiePolicyUrl && (
                  <> and <Link 
                    to={cookiePolicyUrl}
                    onClick={handlePolicyClick}
                    className="policy-link"
                  >
                    Cookie Policy
                  </Link></>
                )}.
              </p>
              <div className="cookie-buttons">
                <button
                  onClick={() => setShowPreferences(true)}
                  className="button-secondary"
                >
                  Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="button-primary"
                >
                  Accept All
                </button>
              </div>
            </div>
          ) : (
                          <div className="preferences-panel">
              <h3>Cookie Preferences</h3>
              <p className="preferences-note">Your preferences can be updated at any time through our Privacy Policy page.</p>
              <div className="preferences-options">
                <div className="preference-item">
                  <input
                    type="checkbox"
                    checked={preferences.necessary}
                    disabled
                    id="necessary"
                  />
                  <label htmlFor="necessary">
                    Necessary (Required) - Essential for the website to function properly
                  </label>
                </div>
                <div className="preference-item">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    id="analytics"
                  />
                  <label htmlFor="analytics">
                    Analytics - Help us understand how visitors interact with our website
                  </label>
                </div>
                <div className="preference-item">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    id="marketing"
                  />
                  <label htmlFor="marketing">
                    Marketing - Used to deliver relevant ads and content
                  </label>
                </div>
              </div>
              <div className="preferences-buttons">
                <button
                  onClick={() => setShowPreferences(false)}
                  className="button-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="button-primary"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;